import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import env from 'Env';
import FluidImg from 'Components/Media/Image/FluidImg';
import imageSizes from 'Helpers/Image/image-sizes';
import 'Components/Media/Image/ProductCover.scss';

export default function ProductCover({
  alt,
  cover, // 16x9 cover
  portraitCover, // 2x3 cover
  coverType, // What type of cover has been uploaded? 16x9 (Standard), Portrait or Square
  displayType,
  dpr,
  lazy,
  folder,
  link,
  sizesOverride,
  thumbnail,
  adaptiveSize, // Used when portrait and square images need to be shown at their natural size, otherwise the image will be shown as 16x9 with a blurred background
}) {
  let coverSize = 'standard';
  if (displayType === 'comic' || displayType === 'graphic-novel' || displayType === 'book' || displayType === 'audio' || displayType === 'video' || displayType === 'software') {
    coverSize = 'unknown';
  }
  if (coverType === '16x9') {
    coverSize = 'standard';
  }
  if (coverType === 'square') {
    coverSize = 'square';
  }
  if (coverType === 'portrait') {
    coverSize = 'portrait';
  }

  const renderCover = () => {
    if (portraitCover && coverSize === 'portrait') {
      return (
        <div className="responsive-image-island product-cover-container portrait-cover">
          <div className="responsive-image responsive-image--2by3">
            <FluidImg
              className="cover-image-foreground"
              src={portraitCover}
              lazy={lazy}
              alt={alt}
              folder={folder}
            />
          </div>
        </div>
      );
    }

    let aspectRatio = '16x9';
    if (coverSize === 'square' && adaptiveSize) aspectRatio = '1x1';
    if (coverSize === 'portrait' && adaptiveSize) aspectRatio = '2x3';

    return (
      <div className={classNames('responsive-image-island product-cover-container', `${coverSize}-cover`, { 'thumbnail-cover': thumbnail })}>
        <div className={classNames('responsive-image', { 'responsive-image--16by9': aspectRatio === '16x9' }, { 'responsive-image--1by1': aspectRatio === '1x1' }, { 'responsive-image--2by3': aspectRatio === '2x3' })}>
          {cover &&
            <React.Fragment>
              {cover.startsWith('facet/') ?
                <img className="img-force-full" alt={alt} src={`${env.apiProtocol}://${env.apiUrl}/image/${cover}`} />
                :
                <React.Fragment>
                  {coverSize === 'standard' &&
                    <FluidImg
                      ratio16by9
                      sizesOverride={sizesOverride}
                      className="img-force-full"
                      src={cover}
                      alt={alt}
                      dpr={dpr}
                      lazy={lazy}
                      folder={folder}
                    />
                  }

                  {coverSize !== 'standard' &&
                    <React.Fragment>
                      <FluidImg
                        className="cover-image-foreground"
                        src={cover}
                        alt={alt}
                        dpr={dpr}
                        lazy={lazy}
                        folder={folder}
                      />
                      <FluidImg
                        className="cover-image-background"
                        src={cover}
                        alt="Blurred background image"
                        lazy={lazy}
                        folder={folder}
                      />
                    </React.Fragment>
                  }
                </React.Fragment>
              }
            </React.Fragment>
          }
        </div>
      </div>
    );
  };

  if (link) {
    return (
      <Link to={link}>
        {renderCover()}
      </Link>
    );
  }

  return (
    renderCover()
  );
}

ProductCover.propTypes = {
  alt: PropTypes.string,
  cover: PropTypes.string,
  coverType: PropTypes.string,
  displayType: PropTypes.string,
  dpr: PropTypes.number,
  folder: PropTypes.string,
  lazy: PropTypes.bool,
  link: PropTypes.string,
  portraitCover: PropTypes.string,
  sizesOverride: PropTypes.string,
  thumbnail: PropTypes.bool,
  adaptiveSize: PropTypes.bool,
};

ProductCover.defaultProps = {
  alt: '',
  cover: '',
  coverType: '',
  displayType: '',
  dpr: 1,
  folder: 'product',
  lazy: false,
  link: null,
  portraitCover: '',
  sizesOverride: imageSizes(3, 4, 4, 6),
  thumbnail: false,
  adaptiveSize: false,
};
